import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { validateMagicLink } from '@/services/platform/data/authentication';
import { consumeDeepLinkTarget } from '@/lib/core/hooks/deepLinkTarget';

const AUTH_DEBUG = import.meta.env.VITE_AUTH_DEBUG === 'true';

export function AuthAccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [diagData, setDiagData] = useState<Record<string, unknown> | null>(null);

  useEffect(() => {
    async function handleAccess() {
      const d: Record<string, unknown> = {
        ts: new Date().toISOString(),
        href: window.location.href,
        search: window.location.search,
      };

      try {
        const token = searchParams.get('token');
        d.tokenPresent = Boolean(token);

        if (!token) {
          d.outcome = 'no_token';
          return;
        }

        const auth = await validateMagicLink(token);

        if (auth) {
          d.outcome = 'success';
          navigate(consumeDeepLinkTarget() ?? '/', { replace: true });
        } else {
          d.outcome = 'invalid_token';
        }
      } catch (error) {
        d.error = String(error);
        console.error('[AuthAccess] error', error);
      } finally {
        console.log('[AuthAccess] diag', JSON.stringify(d));
        setDiagData({ ...d });
        if (d.outcome !== 'success') {
          // Discard any remembered deep-link target — this login attempt failed, so it
          // must not be replayed against an unrelated later session.
          consumeDeepLinkTarget();
          navigate('/', { replace: true });
        }
      }
    }

    handleAccess();
  }, [searchParams, navigate]);

  if (!diagData) {
    return <p style={{ padding: 16, color: '#fff', background: '#000' }}>Authenticating…</p>;
  }

  if (AUTH_DEBUG) {
    return (
      <pre
        style={{
          padding: 16,
          fontSize: 11,
          background: '#000',
          color: '#0f0',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-all',
          overflowY: 'auto',
          height: '100vh',
          margin: 0,
        }}
      >
        {JSON.stringify(diagData, null, 2)}
      </pre>
    );
  }

  return null;
}
